import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BellRing,
  CalendarRange,
  Circle,
  CheckCircle2,
  ChevronRight,
  Eye,
  EyeOff,
  Globe2,
  Layers3,
  Loader2,
  LockKeyhole,
  Mail,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Save,
  Search,
  Shield,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { getPasswordValidationState } from "../../utils/passwordPolicy";
import { toast } from "react-toastify";
import {
  activatePlatformTenant,
  BILLING_MODES,
  buildTenantUpdatePayload,
  createPlatformTenant,
  getBackendErrorMessage,
  getPlatformTenant,
  getTenantEditInitialState,
  listPlatformTenants,
  requestPlatformTenantDeletion,
  runPlatformTenantDeletionJob,
  suspendPlatformTenant,
  TENANT_STATUSES,
  updatePlatformTenant,
} from "../../apiPlatformTenants";
import { getPlatformSmtpConfig, savePlatformSmtpConfig } from "../../apiWithPlatformSmtp";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import PlatformTenantLlmRuntimePanel from "./PlatformTenantLlmRuntimePanel";
import {
  buildSaasOnboardingLlmPayload,
  getSaasCredentialPreview,
  getSaasProviderLabel,
  SAAS_HOSTED_PROVIDERS,
  validateSaasRuntimeDraft,
} from "./platformLlmRuntimeState";

const overviewMetrics = [
  {
    label: "Active customers",
    value: "12",
    delta: "↑ 2 this month",
    tone: "positive",
  },
  {
    label: "Total users",
    value: "348",
    delta: "↑ 41 this month",
    tone: "positive",
  },
  {
    label: "Tokens consumed",
    value: "24.6M",
    delta: "↑ 18% vs last month",
    tone: "positive",
  },
  {
    label: "Demo tenants",
    value: "3",
    delta: "1 needs attention",
    tone: "muted",
  },
];

const tenants = [
  {
    id: "bisleri",
    name: "Bisleri",
    email: "admin@bisleri.example.com",
    status: "active",
    billingMode: "payg",
    users: 64,
    tokens: "5.2M",
    region: "India",
    plan: "Enterprise",
    usageShare: 82,
  },
  {
    id: "tata-nexarc",
    name: "Tata Nexarc",
    email: "admin@tatanexarc.example.com",
    status: "active",
    billingMode: "payg",
    users: 112,
    tokens: "8.8M",
    region: "India",
    plan: "Enterprise",
    usageShare: 100,
  },
  {
    id: "mahindra-finance",
    name: "Mahindra Finance",
    email: "admin@mahindra.example.com",
    status: "active",
    billingMode: "demo",
    users: 18,
    tokens: "48K / 100K",
    region: "India",
    plan: "Demo",
    usageShare: 48,
  },
  {
    id: "reliance-retail",
    name: "Reliance Retail",
    email: "admin@reliance.example.com",
    status: "suspended",
    billingMode: "payg",
    users: 87,
    tokens: "3.1M",
    region: "India",
    plan: "Enterprise",
    usageShare: 43,
  },
];

const recentActivity = [
  {
    title: "Bisleri onboarded",
    detail: "New pay-as-you-go tenant activated",
    time: "2h ago",
    tone: "primary",
  },
  {
    title: "Reliance suspended",
    detail: "Manual suspension requested by platform ops",
    time: "5h ago",
    tone: "danger",
  },
  {
    title: "HDFC demo top-up",
    detail: "+50K demo tokens issued",
    time: "1d ago",
    tone: "success",
  },
  {
    title: "Mahindra converted",
    detail: "Demo moved to pay-as-you-go",
    time: "2d ago",
    tone: "secondary",
  },
];

const weeklyUsage = [
  { day: "Mon", value: 2.1 },
  { day: "Tue", value: 3.4 },
  { day: "Wed", value: 2.8 },
  { day: "Thu", value: 4.1 },
  { day: "Fri", value: 3.9 },
  { day: "Sat", value: 2.2 },
  { day: "Sun", value: 4.6 },
];

const demoQuotaHealth = [
  { name: "Mahindra Finance", pct: 48, detail: "48K of 100K used", tone: "warning" },
  { name: "Wipro Demo", pct: 22, detail: "22K of 100K used", tone: "good" },
  { name: "HDFC Pilot", pct: 71, detail: "71K of 100K used", tone: "risk" },
];

const auditEvents = [
  {
    actor: "Super Admin",
    action: "Created tenant",
    target: "Bisleri",
    scope: "Platform",
    time: "2026-03-25 10:32 UTC",
  },
  {
    actor: "Ops Console",
    action: "Suspended tenant",
    target: "Reliance Retail",
    scope: "Platform",
    time: "2026-03-25 06:10 UTC",
  },
  {
    actor: "Super Admin",
    action: "Updated demo quota",
    target: "HDFC Pilot",
    scope: "Billing",
    time: "2026-03-24 18:21 UTC",
  },
  {
    actor: "Super Admin",
    action: "Rotated platform LLM key",
    target: "Primary workspace",
    scope: "Security",
    time: "2026-03-23 11:08 UTC",
  },
];

const usageByCustomer = [
  { name: "Tata Nexarc", value: 8.8, color: "var(--primary)" },
  { name: "Bisleri", value: 5.2, color: "var(--secondary)" },
  { name: "Reliance", value: 3.1, color: "var(--violet)" },
  { name: "Others", value: 7.5, color: "rgba(99,102,241,0.18)" },
];

const platformAlerts = [
  {
    title: "1 demo tenant is nearing quota exhaustion",
    detail: "Mahindra Finance is at 48K / 100K and trending upward.",
    tone: "warning",
  },
  {
    title: "Suspended tenant pending review",
    detail: "Reliance Retail was suspended 5 hours ago and requires ops confirmation.",
    tone: "danger",
  },
  {
    title: "Weekly platform health remains stable",
    detail: "No incident-level alerts reported across core services this week.",
    tone: "good",
  },
];

const onboardingChecklist = [
  "Create tenant profile and internal owner",
  "Select demo or pay-as-you-go billing",
  "Provision the first tenant admin account",
  "Configure the tenant hosted LLM provider",
  "Review derived slug and namespace preview",
  "Submit the workspace provisioning request",
];

const MAX_PLATFORM_SLUG_LENGTH = 55;

function derivePlatformSlug(companyName) {
  const normalized = String(companyName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

  if (!normalized) {
    return "";
  }

  return normalized
    .slice(0, MAX_PLATFORM_SLUG_LENGTH)
    .replace(/^-+|-+$/g, "");
}

function derivePlatformNamespace(companyName) {
  const slug = derivePlatformSlug(companyName);
  return slug ? `tenant_${slug.replace(/-/g, "_")}` : "";
}

function getCreateTenantPayload(formState) {
  const payload = {
    company_name: String(formState.companyName || "").trim(),
    billing_mode: String(formState.billingMode || "").trim().toLowerCase(),
    admin: {
      name: String(formState.adminName || "").trim(),
      email: String(formState.adminEmail || "").trim(),
      password: String(formState.adminPassword || ""),
    },
    llm: buildSaasOnboardingLlmPayload(formState),
  };

  if (payload.billing_mode === "demo") {
    payload.demo_token_limit = Number(formState.demoTokenLimit);
  }

  return payload;
}

function validateOnboardingForm(formState) {
  const errors = {};
  const passwordChecks = getPasswordValidationState(formState.adminPassword);

  if (!String(formState.companyName || "").trim()) {
    errors.companyName = "Company name is required.";
  }

  if (!String(formState.adminName || "").trim()) {
    errors.adminName = "Admin name is required.";
  }

  if (!String(formState.adminEmail || "").trim()) {
    errors.adminEmail = "Admin email is required.";
  }

  if (!String(formState.adminPassword || "")) {
    errors.adminPassword = "Admin password is required.";
  } else if (!passwordChecks.every((rule) => rule.isValid)) {
    errors.adminPassword = "Password does not meet the required policy.";
  }

  Object.assign(
    errors,
    Object.fromEntries(
      Object.entries(
        validateSaasRuntimeDraft({
          provider: formState.llmProvider,
          requestTimeoutSeconds: formState.llmRequestTimeoutSeconds,
          maxRetries: formState.llmMaxRetries,
        })
      ).map(([key, value]) => [`llm${key.charAt(0).toUpperCase()}${key.slice(1)}`, value])
    )
  );

  if (!String(formState.llmApiKey || "").trim()) {
    errors.llmApiKey = "LLM provider API key is required.";
  }

  if (String(formState.billingMode || "") === "demo") {
    const rawLimit = String(formState.demoTokenLimit || "").trim();
    if (!rawLimit) {
      errors.demoTokenLimit = "Demo token limit is required for demo tenants.";
    } else if (!/^\d+$/.test(rawLimit)) {
      errors.demoTokenLimit = "Demo token limit must be a non-negative integer.";
    }
  }

  if (!derivePlatformSlug(formState.companyName)) {
    errors.companyName = errors.companyName || "Company name must contain at least one letter or number.";
  }

  return errors;
}

function validatePlatformSmtpForm(formState) {
  const errors = {};

  if (!String(formState.host || "").trim()) {
    errors.host = "SMTP host is required.";
  }

  const portValue = String(formState.port || "").trim();
  if (!portValue) {
    errors.port = "SMTP port is required.";
  } else if (!/^\d+$/.test(portValue) || Number(portValue) <= 0) {
    errors.port = "SMTP port must be a positive number.";
  }

  if (!String(formState.username || "").trim()) {
    errors.username = "SMTP username is required.";
  }

  if (!String(formState.password || "")) {
    errors.password = "SMTP password is required to save platform email settings.";
  }

  return errors;
}

function getOnboardingEmailTone(status) {
  if (status === "sent") {
    return "success";
  }
  if (status === "not_configured") {
    return "warning";
  }
  return "error";
}

const settingsCards = [
  {
    title: "Platform monitoring",
    description: "Operational status messaging, incident hooks, and tenant alert routing.",
    action: "Review controls",
    icon: Activity,
  },
  {
    title: "Authentication posture",
    description: "Session, password policy, and future SSO readiness for platform users.",
    action: "View policy",
    icon: LockKeyhole,
  },
  {
    title: "Platform AI configuration",
    description: "Hosted-provider governance, tenant runtime defaults, and future model policy controls.",
    action: "Open panel",
    icon: Sparkles,
  },
];

function useSessionProfile() {
  return useOutletContext() || {};
}

function SectionCard({ title, action, children }) {
  return (
    <section className="platform-card">
      <div className="platform-card-header">
        <div>
          <h3 className="platform-card-title">{title}</h3>
        </div>
        {action ? <div className="platform-card-action">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function MetricCard({ label, value, delta, tone = "muted" }) {
  return (
    <div className="platform-metric-card">
      <div className="platform-metric-label">{label}</div>
      <div className="platform-metric-value">{value}</div>
      <div className={`platform-metric-delta is-${tone}`}>{delta}</div>
    </div>
  );
}

function TenantStatusBadge({ status }) {
  const normalizedStatus = String(status || "").toLowerCase();
  const labels = {
    [TENANT_STATUSES.ACTIVE]: "Active",
    [TENANT_STATUSES.SUSPENDED]: "Suspended",
    [TENANT_STATUSES.PENDING_DELETE]: "Pending delete",
    [TENANT_STATUSES.DELETED]: "Deleted",
  };

  return (
    <span className={`platform-badge is-${normalizedStatus}`}>
      {labels[normalizedStatus] || status || "Unknown"}
    </span>
  );
}

function BillingModeBadge({ mode }) {
  const normalizedMode = String(mode || "").toLowerCase();

  return (
    <span className={`platform-badge is-${normalizedMode}`}>
      {normalizedMode === BILLING_MODES.PAYG ? "Pay as you go" : "Demo"}
    </span>
  );
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "Not set";
  }

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US").format(numberValue);
}

function formatTenantDate(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getTenantTokenLabel(tenant) {
  if (String(tenant?.billing_mode || "").toLowerCase() !== BILLING_MODES.DEMO) {
    return "Not applicable";
  }

  return `${formatNumber(tenant?.demo_token_used || 0)} / ${formatNumber(tenant?.demo_token_limit)}`;
}

function getLatestDeletionJob(tenant) {
  const jobs = Array.isArray(tenant?.deletion_jobs) ? tenant.deletion_jobs : [];
  return jobs[0] || null;
}

function CustomerTable() {
  return (
    <div className="platform-table-wrap">
      <table className="platform-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Mode</th>
            <th>Status</th>
            <th>Users</th>
            <th>Tokens</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((tenant) => (
            <tr key={tenant.id}>
              <td>
                <div className="platform-customer-name">{tenant.name}</div>
                <div className="platform-customer-email">{tenant.email}</div>
              </td>
              <td>
                <BillingModeBadge mode={tenant.billingMode} />
              </td>
              <td>
                <TenantStatusBadge status={tenant.status} />
              </td>
              <td>{tenant.users}</td>
              <td>{tenant.tokens}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageBars() {
  const maxValue = Math.max(...usageByCustomer.map((item) => item.value));

  return (
    <div className="platform-stack">
      {usageByCustomer.map((item) => (
        <div className="platform-bar-row" key={item.name}>
          <div className="platform-bar-label">{item.name}</div>
          <div className="platform-bar-track">
            <div
              className="platform-bar-fill"
              style={{
                width: `${Math.round((item.value / maxValue) * 100)}%`,
                background: item.color,
              }}
            />
          </div>
          <div className="platform-bar-value">{item.value}M</div>
        </div>
      ))}
    </div>
  );
}

function WeeklyUsageChart() {
  const maxValue = Math.max(...weeklyUsage.map((item) => item.value));

  return (
    <div className="platform-weekly-chart">
      <div className="platform-weekly-bars">
        {weeklyUsage.map((item) => (
          <div className="platform-weekly-item" key={item.day}>
            <div
              className="platform-weekly-bar"
              style={{ height: `${Math.max(22, Math.round((item.value / maxValue) * 100))}%` }}
              title={`${item.value}M tokens`}
            />
            <div className="platform-weekly-label">{item.day}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingSplit() {
  return (
    <div className="platform-donut-section">
      <div
        className="platform-donut"
        style={{
          background:
            "conic-gradient(var(--primary) 0deg 270deg, #1D9E75 270deg 360deg)",
        }}
      >
        <div className="platform-donut-hole">
          <strong>12</strong>
          <span>tenants</span>
        </div>
      </div>
      <div className="platform-stack">
        <div className="platform-legend-item">
          <span className="platform-legend-dot" style={{ background: "var(--primary)" }} />
          Pay-as-you-go — 9
        </div>
        <div className="platform-legend-item">
          <span className="platform-legend-dot" style={{ background: "#1D9E75" }} />
          Demo — 3
        </div>
      </div>
    </div>
  );
}

function RecentActivityList() {
  return (
    <div className="platform-stack">
      {recentActivity.map((item) => (
        <div className="platform-activity-row" key={`${item.title}-${item.time}`}>
          <div className={`platform-activity-avatar is-${item.tone}`}>
            {item.title
              .split(" ")
              .slice(0, 2)
              .map((word) => word[0])
              .join("")
              .toUpperCase()}
          </div>
          <div className="platform-activity-copy">
            <div className="platform-activity-title">{item.title}</div>
            <div className="platform-activity-detail">{item.detail}</div>
          </div>
          <div className="platform-activity-time">{item.time}</div>
        </div>
      ))}
    </div>
  );
}

function PageHero({ eyebrow, title, description, actions }) {
  return (
    <div className="platform-hero">
      <div>
        {eyebrow ? <div className="platform-hero-eyebrow">{eyebrow}</div> : null}
        <h1 className="platform-page-title">{title}</h1>
        <p className="platform-page-description">{description}</p>
      </div>
      {actions ? <div className="platform-hero-actions">{actions}</div> : null}
    </div>
  );
}

export function PlatformDashboardPage() {
  const { sessionProfile } = useSessionProfile();
  const userName = sessionProfile?.user?.name || "Super Admin";
  const tenantCount = tenants.length;

  return (
    <div className="platform-page">
      <PageHero
        eyebrow="App control plane"
        title={`Welcome back, ${userName}`}
        description="Platform-wide customer onboarding, quota oversight, and health visibility for Geneilytics SaaS Application."
        actions={
          <>
            <Button variant="outline">
              <BellRing size={16} />
              Alerts
            </Button>
            <Button>
              <Plus size={16} />
              New tenant
            </Button>
          </>
        }
      />

      <div className="platform-metric-grid">
        {overviewMetrics.map((metric) => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </div>

      <div className="platform-overview-grid">
        <SectionCard title="Customer tenants" action={`${tenantCount} total`}>
          <CustomerTable />
        </SectionCard>

        <SectionCard title="Billing mode split">
          <BillingSplit />
          <div className="platform-card-divider" />
          <div className="platform-subsection-title">Token usage · last 7 days</div>
          <WeeklyUsageChart />
        </SectionCard>
      </div>

      <div className="platform-overview-triple">
        <SectionCard title="Usage by customer">
          <UsageBars />
        </SectionCard>

        <SectionCard title="Demo quota health">
          <div className="platform-stack">
            {demoQuotaHealth.map((item) => (
              <div className="platform-quota-row" key={item.name}>
                <div className="platform-quota-head">
                  <span>{item.name}</span>
                  <strong className={`platform-quota-pct is-${item.tone}`}>{item.pct}%</strong>
                </div>
                <div className="platform-progress-track">
                  <div
                    className={`platform-progress-fill is-${item.tone}`}
                    style={{ width: `${item.pct}%` }}
                  />
                </div>
                <div className="platform-quota-detail">{item.detail}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Recent activity">
          <RecentActivityList />
        </SectionCard>
      </div>
    </div>
  );
}

function TenantDetailsDrawer({
  tenantId,
  isOpen,
  onClose,
  onTenantChanged,
  onIncludeDeletedNeeded,
}) {
  const [tenant, setTenant] = useState(null);
  const [formState, setFormState] = useState(getTenantEditInitialState(null));
  const [fieldErrors, setFieldErrors] = useState({});
  const [drawerError, setDrawerError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionInFlight, setActionInFlight] = useState("");

  const loadTenant = useCallback(async () => {
    if (!tenantId) {
      return;
    }

    setIsLoading(true);
    setDrawerError("");
    try {
      const { data } = await getPlatformTenant(tenantId);
      const nextTenant = data?.tenant || null;
      setTenant(nextTenant);
      setFormState(getTenantEditInitialState(nextTenant));
      setFieldErrors({});
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to load tenant details.");
      setDrawerError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (isOpen && tenantId) {
      loadTenant();
    }
  }, [isOpen, loadTenant, tenantId]);

  useEffect(() => {
    if (!isOpen) {
      setTenant(null);
      setDrawerError("");
      setFieldErrors({});
      setActionInFlight("");
    }
  }, [isOpen]);

  const isDeleted = String(tenant?.status || "").toLowerCase() === TENANT_STATUSES.DELETED;
  const isPendingDelete = String(tenant?.status || "").toLowerCase() === TENANT_STATUSES.PENDING_DELETE;
  const latestDeletionJob = getLatestDeletionJob(tenant);
  const initialFormState = getTenantEditInitialState(tenant);
  const hasEditableChanges =
    String(formState.displayName || "") !== String(initialFormState.displayName || "") ||
    String(formState.billingMode || "") !== String(initialFormState.billingMode || "") ||
    String(formState.demoTokenLimit || "") !== String(initialFormState.demoTokenLimit || "");
  const canSave = Boolean(tenant) && !isDeleted && hasEditableChanges && !isSaving;

  const handleFieldChange = (field) => (event) => {
    const nextValue = event.target.value;
    setFormState((current) => ({
      ...current,
      [field]: nextValue,
      ...(field === "billingMode" && nextValue === BILLING_MODES.PAYG
        ? { demoTokenLimit: "" }
        : field === "billingMode" && current.demoTokenLimit === ""
          ? { demoTokenLimit: "10000" }
          : {}),
    }));
    setFieldErrors((current) => {
      if (!current[field] && field !== "billingMode") {
        return current;
      }
      const nextErrors = { ...current };
      delete nextErrors[field];
      if (field === "billingMode") {
        delete nextErrors.demoTokenLimit;
      }
      return nextErrors;
    });
    setDrawerError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!tenant) {
      return;
    }

    const { payload, errors, changed } = buildTenantUpdatePayload(formState, tenant);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setDrawerError("Please fix the highlighted fields before saving.");
      return;
    }
    if (!changed) {
      setDrawerError("No tenant changes to save.");
      return;
    }

    setIsSaving(true);
    setDrawerError("");
    setFieldErrors({});
    try {
      const { data } = await updatePlatformTenant(tenant.id, payload);
      const nextTenant = data?.tenant || tenant;
      setTenant(nextTenant);
      setFormState(getTenantEditInitialState(nextTenant));
      toast.success("Tenant updated successfully.");
      onTenantChanged(nextTenant);
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to update tenant.");
      setDrawerError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const runLifecycleAction = async ({ actionKey, confirmMessage, request, successMessage }) => {
    if (!tenant || actionInFlight) {
      return;
    }
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    setActionInFlight(actionKey);
    setDrawerError("");
    try {
      const { data } = await request();
      const nextTenant = data?.tenant || tenant;
      setTenant((current) => ({
        ...(current || {}),
        ...nextTenant,
        deletion_jobs:
          data?.job && !nextTenant?.deletion_jobs
            ? [data.job]
            : nextTenant?.deletion_jobs || current?.deletion_jobs || [],
      }));
      setFormState(getTenantEditInitialState(nextTenant));
      toast.success(successMessage);
      onTenantChanged(nextTenant);
      if (actionKey === "run-delete") {
        onIncludeDeletedNeeded();
      }
      await loadTenant();
    } catch (error) {
      const message = getBackendErrorMessage(error, "Tenant action failed.");
      setDrawerError(message);
      toast.error(message);
    } finally {
      setActionInFlight("");
    }
  };

  const status = String(tenant?.status || "").toLowerCase();

  if (!isOpen) {
    return null;
  }

  return (
    <div className="platform-drawer-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="platform-tenant-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Tenant details"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="platform-drawer-header">
          <div>
            <div className="platform-hero-eyebrow">Tenant account</div>
            <h2>{tenant?.display_name || "Tenant details"}</h2>
          </div>
          <button
            type="button"
            className="platform-icon-button"
            onClick={onClose}
            aria-label="Close tenant details"
          >
            <X size={16} />
          </button>
        </div>

        {isLoading ? (
          <div className="platform-drawer-empty">
            <Loader2 size={18} className="platform-spin" />
            Loading tenant details...
          </div>
        ) : null}

        {!isLoading && drawerError ? (
          <div className="platform-inline-alert is-error">{drawerError}</div>
        ) : null}

        {!isLoading && tenant ? (
          <div className="platform-drawer-body">
            <div className="platform-drawer-badge-row">
              <TenantStatusBadge status={tenant.status} />
              <BillingModeBadge mode={tenant.billing_mode} />
            </div>

            <form className="platform-stack" onSubmit={handleSave}>
              <div className="platform-form-grid platform-form-grid--single">
                <label className="platform-field">
                  <span>Display name <span className="platform-required">*</span></span>
                  <Input
                    value={formState.displayName}
                    onChange={handleFieldChange("displayName")}
                    disabled={isDeleted}
                    error={Boolean(fieldErrors.displayName)}
                  />
                  {fieldErrors.displayName ? (
                    <small className="platform-field-error">{fieldErrors.displayName}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Account type <span className="platform-required">*</span></span>
                  <select
                    className={`platform-select ${fieldErrors.billingMode ? "is-error" : ""}`}
                    value={formState.billingMode}
                    onChange={handleFieldChange("billingMode")}
                    disabled={isDeleted}
                  >
                    <option value={BILLING_MODES.DEMO}>Demo</option>
                    <option value={BILLING_MODES.PAYG}>Pay as you go</option>
                  </select>
                  {fieldErrors.billingMode ? (
                    <small className="platform-field-error">{fieldErrors.billingMode}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>
                    Demo token limit
                    {formState.billingMode === BILLING_MODES.DEMO ? (
                      <span className="platform-required"> *</span>
                    ) : null}
                  </span>
                  <Input
                    value={formState.demoTokenLimit}
                    onChange={handleFieldChange("demoTokenLimit")}
                    disabled={isDeleted || formState.billingMode !== BILLING_MODES.DEMO}
                    placeholder={formState.billingMode === BILLING_MODES.DEMO ? "10000" : "Not applicable"}
                    inputMode="numeric"
                    error={Boolean(fieldErrors.demoTokenLimit)}
                  />
                  {fieldErrors.demoTokenLimit ? (
                    <small className="platform-field-error">{fieldErrors.demoTokenLimit}</small>
                  ) : null}
                </label>
              </div>

              <div className="platform-card-actions platform-card-actions--end">
                <Button type="submit" disabled={!canSave}>
                  {isSaving ? <Loader2 size={16} className="platform-spin" /> : <Save size={16} />}
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </form>

            <div className="platform-card-divider" />

            <div className="platform-drawer-section">
              <h3 className="platform-subsection-title">Lifecycle</h3>
              <div className="platform-card-actions platform-card-actions--start">
                {status === TENANT_STATUSES.ACTIVE ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={Boolean(actionInFlight)}
                    onClick={() =>
                      runLifecycleAction({
                        actionKey: "suspend",
                        confirmMessage: `Suspend ${tenant.display_name}? Tenant users will be blocked until reactivated.`,
                        request: () => suspendPlatformTenant(tenant.id),
                        successMessage: "Tenant suspended.",
                      })
                    }
                  >
                    {actionInFlight === "suspend" ? (
                      <Loader2 size={16} className="platform-spin" />
                    ) : (
                      <PowerOff size={16} />
                    )}
                    Suspend
                  </Button>
                ) : null}

                {status === TENANT_STATUSES.SUSPENDED ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={Boolean(actionInFlight)}
                    onClick={() =>
                      runLifecycleAction({
                        actionKey: "activate",
                        request: () => activatePlatformTenant(tenant.id),
                        successMessage: "Tenant activated.",
                      })
                    }
                  >
                    {actionInFlight === "activate" ? (
                      <Loader2 size={16} className="platform-spin" />
                    ) : (
                      <Power size={16} />
                    )}
                    Activate
                  </Button>
                ) : null}

                {!isDeleted && !isPendingDelete ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={Boolean(actionInFlight)}
                    onClick={() =>
                      runLifecycleAction({
                        actionKey: "request-delete",
                        confirmMessage: `Request deletion for ${tenant.display_name}? This will move the tenant to pending delete.`,
                        request: () => requestPlatformTenantDeletion(tenant.id),
                        successMessage: "Tenant deletion requested.",
                      })
                    }
                  >
                    {actionInFlight === "request-delete" ? (
                      <Loader2 size={16} className="platform-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Request deletion
                  </Button>
                ) : null}
              </div>

              {latestDeletionJob ? (
                <div className="platform-inline-alert is-warning">
                  <div className="platform-inline-alert-copy">
                    <strong>Deletion job #{latestDeletionJob.id}: {latestDeletionJob.status}</strong>
                    <span>
                      {latestDeletionJob.error_message ||
                        `Created ${formatTenantDate(latestDeletionJob.created_at)}`}
                    </span>
                  </div>
                  {isPendingDelete && latestDeletionJob.status !== "completed" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={Boolean(actionInFlight)}
                      onClick={() =>
                        runLifecycleAction({
                          actionKey: "run-delete",
                          confirmMessage:
                            `Run final cleanup for ${tenant.display_name}? This drops tenant resources and cannot be undone.`,
                          request: () => runPlatformTenantDeletionJob(tenant.id, latestDeletionJob.id),
                          successMessage: "Tenant cleanup completed.",
                        })
                      }
                    >
                      {actionInFlight === "run-delete" ? (
                        <Loader2 size={16} className="platform-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Run cleanup
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="platform-card-divider" />

            {!isDeleted ? (
              <>
                <PlatformTenantLlmRuntimePanel tenantId={tenant.id} />

                <div className="platform-card-divider" />
              </>
            ) : null}

            <div className="platform-drawer-section">
              <h3 className="platform-subsection-title">Metadata</h3>
              <div className="platform-preview-card">
                <div className="platform-preview-row">
                  <span>ID</span>
                  <strong>{tenant.id}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Slug</span>
                  <strong>{tenant.slug}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Namespace</span>
                  <strong>{tenant.namespace_name}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Tenant auth version</span>
                  <strong>{tenant.tenant_auth_version}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Demo token usage</span>
                  <strong>{getTenantTokenLabel(tenant)}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Created</span>
                  <strong>{formatTenantDate(tenant.created_at)}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Updated</span>
                  <strong>{formatTenantDate(tenant.updated_at)}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Disabled</span>
                  <strong>{formatTenantDate(tenant.disabled_at)}</strong>
                </div>
                <div className="platform-preview-row">
                  <span>Deleted</span>
                  <strong>{formatTenantDate(tenant.deleted_at)}</strong>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

export function PlatformCustomersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [billingFilter, setBillingFilter] = useState("all");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [tenantsList, setTenantsList] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const refreshTenants = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const { data } = await listPlatformTenants({ includeDeleted });
      setTenantsList(Array.isArray(data?.tenants) ? data.tenants : []);
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to load customer tenants.");
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    refreshTenants();
  }, [refreshTenants]);

  useEffect(() => {
    const tenantIdFromState = location.state?.selectedTenantId;
    if (tenantIdFromState) {
      setSelectedTenantId(tenantIdFromState);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const filteredTenants = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenantsList.filter((tenant) => {
      const status = String(tenant.status || "").toLowerCase();
      const billingMode = String(tenant.billing_mode || "").toLowerCase();
      const matchesQuery =
        !q ||
        [
          tenant.display_name,
          tenant.slug,
          tenant.namespace_name,
          tenant.status,
          tenant.billing_mode,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesBilling = billingFilter === "all" || billingMode === billingFilter;
      return matchesQuery && matchesStatus && matchesBilling;
    });
  }, [billingFilter, query, statusFilter, tenantsList]);

  const handleTenantChanged = (updatedTenant) => {
    if (!updatedTenant?.id) {
      refreshTenants();
      return;
    }

    setTenantsList((current) => {
      const exists = current.some((tenant) => Number(tenant.id) === Number(updatedTenant.id));
      if (!exists) {
        return [updatedTenant, ...current];
      }
      return current.map((tenant) =>
        Number(tenant.id) === Number(updatedTenant.id) ? { ...tenant, ...updatedTenant } : tenant
      );
    });
  };

  return (
    <div className="platform-page">
      <PageHero
        eyebrow="Customer management"
        title="Tenant management"
        description="List tenants, update account type and demo limits, and manage activation or deletion state."
        actions={
          <>
            <Button variant="outline" onClick={refreshTenants} disabled={isLoading}>
              <RefreshCw size={16} className={isLoading ? "platform-spin" : ""} />
              Refresh
            </Button>
            <Button onClick={() => navigate("/platform/onboarding")}>
              <UserPlus size={16} />
              Onboard customer
            </Button>
          </>
        }
      />

      <SectionCard
        title="Tenant directory"
        action={
          <div className="platform-inline-meta">
            <span>{filteredTenants.length} visible</span>
            <span>{tenantsList.length} loaded</span>
          </div>
        }
      >
        <div className="platform-toolbar">
          <div className="platform-search">
            <Search size={16} />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, slug, namespace, status"
              aria-label="Search tenants"
            />
          </div>
          <div className="platform-filter-controls">
            <select
              className="platform-select platform-select--compact"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Filter by tenant status"
            >
              <option value="all">All statuses</option>
              <option value={TENANT_STATUSES.ACTIVE}>Active</option>
              <option value={TENANT_STATUSES.SUSPENDED}>Suspended</option>
              <option value={TENANT_STATUSES.PENDING_DELETE}>Pending delete</option>
              <option value={TENANT_STATUSES.DELETED}>Deleted</option>
            </select>
            <select
              className="platform-select platform-select--compact"
              value={billingFilter}
              onChange={(event) => setBillingFilter(event.target.value)}
              aria-label="Filter by account type"
            >
              <option value="all">All account types</option>
              <option value={BILLING_MODES.DEMO}>Demo</option>
              <option value={BILLING_MODES.PAYG}>Pay as you go</option>
            </select>
            <label className="platform-checkbox-row">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(event) => setIncludeDeleted(event.target.checked)}
              />
              Include deleted
            </label>
          </div>
        </div>

        {loadError ? (
          <div className="platform-inline-alert is-error">
            <div className="platform-inline-alert-copy">
              <strong>Could not load tenants</strong>
              <span>{loadError}</span>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={refreshTenants}>
              Retry
            </Button>
          </div>
        ) : null}

        {isLoading ? (
          <div className="platform-table-state">
            <Loader2 size={18} className="platform-spin" />
            Loading tenants...
          </div>
        ) : null}

        {!isLoading && filteredTenants.length === 0 ? (
          <div className="platform-table-state">
            No tenants match the current filters.
          </div>
        ) : null}

        {!isLoading && filteredTenants.length > 0 ? (
          <div className="platform-table-wrap">
            <table className="platform-table platform-tenant-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Slug</th>
                  <th>Namespace</th>
                  <th>Status</th>
                  <th>Account type</th>
                  <th>Demo tokens</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <div className="platform-customer-name">{tenant.display_name}</div>
                      <div className="platform-customer-email">ID {tenant.id}</div>
                    </td>
                    <td>{tenant.slug}</td>
                    <td>{tenant.namespace_name}</td>
                    <td>
                      <TenantStatusBadge status={tenant.status} />
                    </td>
                    <td>
                      <BillingModeBadge mode={tenant.billing_mode} />
                    </td>
                    <td>{getTenantTokenLabel(tenant)}</td>
                    <td>{formatTenantDate(tenant.created_at)}</td>
                    <td>{formatTenantDate(tenant.updated_at)}</td>
                    <td>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedTenantId(tenant.id)}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SectionCard>

      <TenantDetailsDrawer
        tenantId={selectedTenantId}
        isOpen={Boolean(selectedTenantId)}
        onClose={() => setSelectedTenantId(null)}
        onTenantChanged={handleTenantChanged}
        onIncludeDeletedNeeded={() => setIncludeDeleted(true)}
      />
    </div>
  );
}

export function PlatformOnboardingPage() {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    companyName: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    billingMode: "demo",
    demoTokenLimit: "10000",
    llmProvider: "groq",
    llmCredentialName: "",
    llmApiKey: "",
    llmRequestTimeoutSeconds: "60",
    llmMaxRetries: "2",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [createdTenant, setCreatedTenant] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  const derivedSlug = useMemo(
    () => derivePlatformSlug(formState.companyName),
    [formState.companyName]
  );
  const derivedNamespace = useMemo(
    () => derivePlatformNamespace(formState.companyName),
    [formState.companyName]
  );
  const llmCredentialPreview = useMemo(
    () =>
      getSaasCredentialPreview({
        companyName: formState.companyName,
        provider: formState.llmProvider,
        displayName: formState.llmCredentialName,
      }),
    [formState.companyName, formState.llmCredentialName, formState.llmProvider]
  );
  const passwordChecks = useMemo(
    () => getPasswordValidationState(formState.adminPassword),
    [formState.adminPassword]
  );
  const isPasswordValid = passwordChecks.every((rule) => rule.isValid);
  const createdTenantEmailState = createdTenant?.onboarding_email || null;

  const handleFieldChange = (field) => (event) => {
    const nextValue = event.target.value;
    setFormState((current) => ({
      ...current,
      [field]: nextValue,
      ...(field === "billingMode" && nextValue === "payg"
        ? { demoTokenLimit: "" }
        : field === "billingMode" && current.demoTokenLimit === ""
          ? { demoTokenLimit: "10000" }
          : {}),
    }));

    setFieldErrors((current) => {
      if (!current[field] && !(field === "billingMode" || field === "companyName")) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[field];
      if (field === "billingMode") {
        delete nextErrors.demoTokenLimit;
      }
      if (field === "companyName") {
        delete nextErrors.companyName;
      }
      return nextErrors;
    });
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateOnboardingForm(formState);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError("Please fix the highlighted fields before creating the tenant.");
      return;
    }

    const payload = getCreateTenantPayload(formState);
    setIsSubmitting(true);
    setFieldErrors({});
    setSubmitError("");

    try {
      const { data } = await createPlatformTenant(payload);
      setCreatedTenant(data);
      setFormState((current) => ({
        ...current,
        adminPassword: "",
        llmApiKey: "",
      }));
      if (data?.onboarding_email?.status === "sent") {
        toast.success("Customer tenant created and onboarding email sent.");
      } else if (data?.onboarding_email?.status === "not_configured") {
        toast.warn("Customer tenant created, but platform SMTP is not configured.");
      } else if (data?.onboarding_email?.status === "failed") {
        toast.warn("Customer tenant created, but the onboarding email could not be sent.");
      } else {
        toast.success("Customer tenant created successfully.");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        "Failed to create the customer tenant.";
      setSubmitError(backendMessage);
      toast.error(backendMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="platform-page">
      <PageHero
        eyebrow="Customer onboarding"
        title="Onboard a new customer"
        description="Provision a new tenant workspace."
      />

      <div className="platform-two-column">
        <SectionCard title="Tenant provisioning request">
          <form className="platform-stack" onSubmit={handleSubmit}>
            <div className="platform-form-section">
              <div className="platform-form-section-head">
                <h4 className="platform-subsection-title">Customer workspace</h4>
              </div>

              <div className="platform-form-grid">
                <label className="platform-field platform-field--wide">
                  <span>Company name <span className="platform-required">*</span></span>
                  <Input
                    value={formState.companyName}
                    onChange={handleFieldChange("companyName")}
                    placeholder="Customer company"
                    error={Boolean(fieldErrors.companyName)}
                  />
                  {fieldErrors.companyName ? (
                    <small className="platform-field-error">{fieldErrors.companyName}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Billing mode <span className="platform-required">*</span></span>
                  <select
                    className={`platform-select ${fieldErrors.billingMode ? "is-error" : ""}`}
                    value={formState.billingMode}
                    onChange={handleFieldChange("billingMode")}
                  >
                    <option value="demo">Demo</option>
                    <option value="payg">Pay as you go</option>
                  </select>
                </label>

                <label className="platform-field">
                  <span>
                    Demo token limit
                    {formState.billingMode === "demo" ? <span className="platform-required"> *</span> : null}
                  </span>
                  <Input
                    value={formState.demoTokenLimit}
                    onChange={handleFieldChange("demoTokenLimit")}
                    placeholder={formState.billingMode === "demo" ? "10000" : "Not required for payg"}
                    disabled={formState.billingMode !== "demo"}
                    error={Boolean(fieldErrors.demoTokenLimit)}
                    inputMode="numeric"
                  />
                  {fieldErrors.demoTokenLimit ? (
                    <small className="platform-field-error">{fieldErrors.demoTokenLimit}</small>
                  ) : null}
                </label>
              </div>
            </div>

            <div className="platform-form-section">
              <div className="platform-form-section-head">
                <h4 className="platform-subsection-title">Initial tenant admin</h4>
              </div>

              <div className="platform-form-grid">
                <label className="platform-field">
                  <span>Admin name <span className="platform-required">*</span></span>
                  <Input
                    value={formState.adminName}
                    onChange={handleFieldChange("adminName")}
                    placeholder="Customer admin"
                    error={Boolean(fieldErrors.adminName)}
                  />
                  {fieldErrors.adminName ? (
                    <small className="platform-field-error">{fieldErrors.adminName}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Admin email <span className="platform-required">*</span></span>
                  <Input
                    type="email"
                    value={formState.adminEmail}
                    onChange={handleFieldChange("adminEmail")}
                    placeholder="admin@customer.example.com"
                    error={Boolean(fieldErrors.adminEmail)}
                  />
                  {fieldErrors.adminEmail ? (
                    <small className="platform-field-error">{fieldErrors.adminEmail}</small>
                  ) : null}
                </label>

                <label className="platform-field platform-field--wide">
                  <span>Admin password <span className="platform-required">*</span></span>
                  <div className="platform-password-input">
                    <Input
                      type={showAdminPassword ? "text" : "password"}
                      value={formState.adminPassword}
                      onChange={handleFieldChange("adminPassword")}
                      placeholder="Temporary password"
                      error={Boolean(fieldErrors.adminPassword)}
                      className="platform-password-input-field"
                    />
                    <button
                      type="button"
                      className="platform-password-toggle"
                      onClick={() => setShowAdminPassword((current) => !current)}
                      aria-label={showAdminPassword ? "Hide password" : "Show password"}
                    >
                      {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="platform-password-policy">
                    <div className="platform-password-policy-title">Password must include:</div>
                    <div className="platform-password-checklist">
                      {passwordChecks.map((rule) => (
                        <div
                          className={`platform-password-rule ${rule.isValid ? "is-valid" : "is-invalid"}`}
                          key={rule.key}
                        >
                          {rule.isValid ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          <span>{rule.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {fieldErrors.adminPassword ? (
                    <small className="platform-field-error">{fieldErrors.adminPassword}</small>
                  ) : null}
                </label>
              </div>
            </div>

            <div className="platform-form-section">
              <div className="platform-form-section-head">
                <h4 className="platform-subsection-title">LLM configuration</h4>
                <p>Select the hosted provider and store the first tenant runtime key.</p>
              </div>

              <div className="platform-form-grid">
                <label className="platform-field platform-field--wide">
                  <span>LLM provider <span className="platform-required">*</span></span>
                  <select
                    className={`platform-select ${fieldErrors.llmProvider ? "is-error" : ""}`}
                    value={formState.llmProvider}
                    onChange={handleFieldChange("llmProvider")}
                  >
                    {SAAS_HOSTED_PROVIDERS.map((provider) => (
                      <option value={provider.provider} key={provider.provider}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.llmProvider ? (
                    <small className="platform-field-error">{fieldErrors.llmProvider}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Credential display name</span>
                  <Input
                    value={formState.llmCredentialName}
                    onChange={handleFieldChange("llmCredentialName")}
                    placeholder={llmCredentialPreview}
                  />
                </label>

                <label className="platform-field">
                  <span>{getSaasProviderLabel(formState.llmProvider)} API key <span className="platform-required">*</span></span>
                  <Input
                    type="password"
                    value={formState.llmApiKey}
                    onChange={handleFieldChange("llmApiKey")}
                    placeholder={`Enter ${getSaasProviderLabel(formState.llmProvider)} API key`}
                    error={Boolean(fieldErrors.llmApiKey)}
                  />
                  {fieldErrors.llmApiKey ? (
                    <small className="platform-field-error">{fieldErrors.llmApiKey}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Request timeout seconds</span>
                  <Input
                    value={formState.llmRequestTimeoutSeconds}
                    onChange={handleFieldChange("llmRequestTimeoutSeconds")}
                    placeholder="60"
                    inputMode="numeric"
                    error={Boolean(fieldErrors.llmRequestTimeoutSeconds)}
                  />
                  {fieldErrors.llmRequestTimeoutSeconds ? (
                    <small className="platform-field-error">{fieldErrors.llmRequestTimeoutSeconds}</small>
                  ) : null}
                </label>

                <label className="platform-field">
                  <span>Max retries</span>
                  <Input
                    value={formState.llmMaxRetries}
                    onChange={handleFieldChange("llmMaxRetries")}
                    placeholder="2"
                    inputMode="numeric"
                    error={Boolean(fieldErrors.llmMaxRetries)}
                  />
                  {fieldErrors.llmMaxRetries ? (
                    <small className="platform-field-error">{fieldErrors.llmMaxRetries}</small>
                  ) : null}
                </label>
              </div>
            </div>

            {submitError ? <div className="platform-inline-alert is-error">{submitError}</div> : null}

            <div className="platform-card-divider" />

            <div className="platform-card-actions platform-card-actions--end">
              <Button type="submit" disabled={isSubmitting || !isPasswordValid}>
                {isSubmitting ? <Loader2 size={16} className="platform-spin" /> : <ArrowRight size={16} />}
                {isSubmitting ? "Onboarding customer..." : "Onboard customer"}
              </Button>
            </div>
          </form>
        </SectionCard>

        <div className="platform-stack">
          <SectionCard title="Readiness checklist">
            <div className="platform-stack">
              {onboardingChecklist.map((item) => (
                <div className="platform-check-row" key={item}>
                  <CheckCircle2 size={16} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Provisioning preview">
            <div className="platform-preview-card">
              <div className="platform-preview-row">
                <span>Tenant URL slug</span>
                <strong>{derivedSlug || "tenant-slug-preview"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Namespace</span>
                <strong>{derivedNamespace || "tenant_slug_preview"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Mode</span>
                <strong>{formState.billingMode === "payg" ? "Pay as you go" : "Demo"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Initial admin</span>
                <strong>{formState.adminEmail || "admin@example.com"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Demo token limit</span>
                <strong>
                  {formState.billingMode === "demo"
                    ? `${formState.demoTokenLimit || "0"} tokens`
                    : "Not applicable"}
                </strong>
              </div>
              <div className="platform-preview-row">
                <span>LLM provider</span>
                <strong>{getSaasProviderLabel(formState.llmProvider)}</strong>
              </div>
              <div className="platform-preview-row">
                <span>LLM credential</span>
                <strong>{llmCredentialPreview}</strong>
              </div>
            </div>
          </SectionCard>

          {createdTenantEmailState ? (
            <SectionCard title="Onboarding email status">
              <div
                className={`platform-inline-alert is-${getOnboardingEmailTone(
                  createdTenantEmailState.status
                )}`}
              >
                <div className="platform-inline-alert-copy">
                  <strong>
                    {createdTenantEmailState.status === "sent"
                      ? "Tenant admin email sent"
                      : createdTenantEmailState.status === "not_configured"
                        ? "Tenant created without onboarding email"
                        : "Tenant created but onboarding email failed"}
                  </strong>
                  <span>
                    {createdTenantEmailState.message ||
                      "The onboarding email result was returned by the backend."}
                  </span>
                </div>
                {createdTenantEmailState.status === "not_configured" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/platform/settings#platform-smtp")}
                  >
                    Configure platform SMTP
                  </Button>
                ) : null}
                {createdTenant?.tenant?.id ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      navigate("/platform/customers", {
                        state: { selectedTenantId: createdTenant.tenant.id },
                      })
                    }
                  >
                    View tenant
                  </Button>
                ) : null}
              </div>
            </SectionCard>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function PlatformBillingPage() {
  return (
    <div className="platform-page">
      <PageHero
        eyebrow="Billing and quotas"
        title="Billing & quota oversight"
        description="Track pay-as-you-go usage, demo budget health, and account-level quota risks across all SaaS customers."
      />

      <div className="platform-metric-grid platform-metric-grid--compact">
        <MetricCard label="Monthly platform usage" value="24.6M" delta="Forecast +6.4%" tone="positive" />
        <MetricCard label="Demo token liability" value="300K" delta="3 active demos" tone="muted" />
        <MetricCard label="Near-limit tenants" value="2" delta="Needs review today" tone="warning" />
      </div>

      <div className="platform-two-column">
        <SectionCard title="Usage concentration">
          <UsageBars />
        </SectionCard>

        <SectionCard title="Platform alerts">
          <div className="platform-stack">
            {platformAlerts.map((alert) => (
              <div className={`platform-alert-card is-${alert.tone}`} key={alert.title}>
                <AlertTriangle size={18} />
                <div>
                  <div className="platform-alert-title">{alert.title}</div>
                  <div className="platform-alert-detail">{alert.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Demo quota health board">
        <div className="platform-three-column">
          {demoQuotaHealth.map((item) => (
            <div className="platform-health-card" key={item.name}>
              <div className="platform-quota-head">
                <span>{item.name}</span>
                <strong className={`platform-quota-pct is-${item.tone}`}>{item.pct}%</strong>
              </div>
              <div className="platform-progress-track">
                <div
                  className={`platform-progress-fill is-${item.tone}`}
                  style={{ width: `${item.pct}%` }}
                />
              </div>
              <div className="platform-quota-detail">{item.detail}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

export function PlatformAuditPage() {
  const [query, setQuery] = useState("");

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return auditEvents;
    return auditEvents.filter((event) =>
      [event.actor, event.action, event.target, event.scope]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [query]);

  return (
    <div className="platform-page">
      <PageHero
        eyebrow="Audit visibility"
        title="Platform audit log"
        description="A static preview of the platform-level activity log for customer lifecycle and governance actions."
      />

      <SectionCard title="Events" action={`${filteredEvents.length} records`}>
        <div className="platform-toolbar">
          <div className="platform-search">
            <Search size={16} />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search actor, action, or target"
            />
          </div>
        </div>

        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Scope</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((event) => (
                <tr key={`${event.actor}-${event.action}-${event.time}`}>
                  <td>{event.actor}</td>
                  <td>{event.action}</td>
                  <td>{event.target}</td>
                  <td>{event.scope}</td>
                  <td>{event.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

export function PlatformSettingsPage() {
  const location = useLocation();
  const smtpSectionRef = useRef(null);
  const [smtpForm, setSmtpForm] = useState({
    host: "",
    port: "",
    username: "",
    password: "",
    fromEmail: "",
    fromName: "",
    use_tls: true,
    use_ssl: false,
  });
  const [smtpErrors, setSmtpErrors] = useState({});
  const [smtpMeta, setSmtpMeta] = useState({
    passwordPresent: false,
    createdDate: null,
    modifiedDate: null,
  });
  const [isLoadingSmtp, setIsLoadingSmtp] = useState(true);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);

  useEffect(() => {
    const fetchPlatformSmtp = async () => {
      setIsLoadingSmtp(true);
      try {
        const { data } = await getPlatformSmtpConfig();
        setSmtpForm({
          host: data?.host || "",
          port: data?.port ? String(data.port) : "",
          username: data?.username || "",
          password: "",
          fromEmail: data?.fromEmail || "",
          fromName: data?.fromName || "",
          use_tls: data?.use_tls ?? true,
          use_ssl: data?.use_ssl ?? false,
        });
        setSmtpMeta({
          passwordPresent: Boolean(data?.password_present),
          createdDate: data?.created_date || null,
          modifiedDate: data?.modified_date || null,
        });
      } catch (error) {
        const message =
          error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Failed to load platform SMTP configuration.";
        toast.error(message);
      } finally {
        setIsLoadingSmtp(false);
      }
    };

    fetchPlatformSmtp();
  }, []);

  useEffect(() => {
    if (location.hash !== "#platform-smtp" || !smtpSectionRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      smtpSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);

    return () => clearTimeout(timeoutId);
  }, [location.hash]);

  const handleSmtpFieldChange = (field) => (event) => {
    const nextValue =
      typeof event === "boolean" ? event : event?.target?.type === "checkbox" ? event.target.checked : event.target.value;

    setSmtpForm((current) => ({
      ...current,
      [field]: nextValue,
    }));
    setSmtpErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const handleSavePlatformSmtp = async (event) => {
    event.preventDefault();

    const validationErrors = validatePlatformSmtpForm(smtpForm);
    if (Object.keys(validationErrors).length > 0) {
      setSmtpErrors(validationErrors);
      return;
    }

    setIsSavingSmtp(true);
    setSmtpErrors({});

    try {
      const payload = {
        host: String(smtpForm.host || "").trim(),
        port: Number(smtpForm.port),
        username: String(smtpForm.username || "").trim(),
        password: smtpForm.password,
        fromEmail: String(smtpForm.fromEmail || "").trim(),
        fromName: String(smtpForm.fromName || "").trim(),
        use_tls: Boolean(smtpForm.use_tls),
        use_ssl: Boolean(smtpForm.use_ssl),
      };

      const { data } = await savePlatformSmtpConfig(payload);
      toast.success(data?.message || "Platform SMTP configuration saved successfully.");
      setSmtpMeta((current) => ({
        ...current,
        passwordPresent: true,
        modifiedDate: new Date().toISOString(),
      }));
      setSmtpForm((current) => ({
        ...current,
        password: "",
      }));
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to save platform SMTP configuration.";
      toast.error(message);
    } finally {
      setIsSavingSmtp(false);
    }
  };

  return (
    <div className="platform-page">
      <PageHero
        eyebrow="Platform settings"
        title="Platform settings"
        description="Configure platform-wide controls for superadmin operations and tenant onboarding delivery."
      />

      <SectionCard
        title="Platform SMTP"
        action={
          <div className="platform-inline-link">
            <Mail size={14} />
            Email delivery for customer onboarding
          </div>
        }
      >
        <div className="platform-two-column platform-two-column--balanced" ref={smtpSectionRef} id="platform-smtp">
          <form className="platform-stack" onSubmit={handleSavePlatformSmtp}>
            <div className="platform-form-grid">
              <label className="platform-field">
                <span>SMTP host <span className="platform-required">*</span></span>
                <Input
                  value={smtpForm.host}
                  onChange={handleSmtpFieldChange("host")}
                  placeholder="smtp.example.com"
                  error={Boolean(smtpErrors.host)}
                  disabled={isLoadingSmtp}
                />
                {smtpErrors.host ? (
                  <small className="platform-field-error">{smtpErrors.host}</small>
                ) : null}
              </label>

              <label className="platform-field">
                <span>Port <span className="platform-required">*</span></span>
                <Input
                  value={smtpForm.port}
                  onChange={handleSmtpFieldChange("port")}
                  placeholder="587"
                  inputMode="numeric"
                  error={Boolean(smtpErrors.port)}
                  disabled={isLoadingSmtp}
                />
                {smtpErrors.port ? (
                  <small className="platform-field-error">{smtpErrors.port}</small>
                ) : null}
              </label>

              <label className="platform-field">
                <span>Username <span className="platform-required">*</span></span>
                <Input
                  type="email"
                  value={smtpForm.username}
                  onChange={handleSmtpFieldChange("username")}
                  placeholder="noreply@example.com"
                  error={Boolean(smtpErrors.username)}
                  disabled={isLoadingSmtp}
                />
                {smtpErrors.username ? (
                  <small className="platform-field-error">{smtpErrors.username}</small>
                ) : null}
              </label>

              <label className="platform-field">
                <span>Password <span className="platform-required">*</span></span>
                <Input
                  type="password"
                  value={smtpForm.password}
                  onChange={handleSmtpFieldChange("password")}
                  placeholder={
                    smtpMeta.passwordPresent
                      ? "Re-enter SMTP password to save changes"
                      : "Enter SMTP password"
                  }
                  error={Boolean(smtpErrors.password)}
                  disabled={isLoadingSmtp}
                />
                {smtpErrors.password ? (
                  <small className="platform-field-error">{smtpErrors.password}</small>
                ) : (
                  <small className="platform-field-hint">
                    {smtpMeta.passwordPresent
                      ? "A platform SMTP password is already configured. Re-enter it whenever you save changes."
                      : "This password is used only for platform onboarding emails."}
                  </small>
                )}
              </label>

              <label className="platform-field">
                <span>From email</span>
                <Input
                  type="email"
                  value={smtpForm.fromEmail}
                  onChange={handleSmtpFieldChange("fromEmail")}
                  placeholder="noreply@example.com"
                  disabled={isLoadingSmtp}
                />
              </label>

              <label className="platform-field">
                <span>From name</span>
                <Input
                  value={smtpForm.fromName}
                  onChange={handleSmtpFieldChange("fromName")}
                  placeholder="Geneilytics Platform"
                  disabled={isLoadingSmtp}
                />
              </label>
            </div>

            <div className="platform-form-section">
              <div className="platform-form-section-head">
                <h4 className="platform-subsection-title">Encryption</h4>
              </div>
              <div className="platform-radio-row">
                {[
                  { label: "TLS", tls: true, ssl: false },
                  { label: "SSL", tls: false, ssl: true },
                  { label: "None", tls: false, ssl: false },
                ].map((option) => {
                  const isActive =
                    smtpForm.use_tls === option.tls && smtpForm.use_ssl === option.ssl;

                  return (
                    <button
                      key={option.label}
                      type="button"
                      className={`platform-choice-chip ${isActive ? "is-active" : ""}`}
                      onClick={() => {
                        setSmtpForm((current) => ({
                          ...current,
                          use_tls: option.tls,
                          use_ssl: option.ssl,
                        }));
                      }}
                      disabled={isLoadingSmtp}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="platform-card-divider" />

            <div className="platform-card-actions platform-card-actions--end">
              <Button type="submit" disabled={isLoadingSmtp || isSavingSmtp}>
                {isSavingSmtp ? <Loader2 size={16} className="platform-spin" /> : <Mail size={16} />}
                {isSavingSmtp ? "Saving platform SMTP..." : "Save platform SMTP"}
              </Button>
            </div>
          </form>

          <div className="platform-stack">
            <div className="platform-preview-card">
              <div className="platform-preview-row">
                <span>Purpose</span>
                <strong>Tenant admin onboarding email</strong>
              </div>
              <div className="platform-preview-row">
                <span>Password configured</span>
                <strong>{smtpMeta.passwordPresent ? "Yes" : "No"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Created</span>
                <strong>{smtpMeta.createdDate || "Not configured yet"}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Last updated</span>
                <strong>{smtpMeta.modifiedDate || "Not configured yet"}</strong>
              </div>
            </div>

            <div className="platform-inline-alert is-warning">
              <div className="platform-inline-alert-copy">
                <strong>Onboarding depends on this configuration</strong>
                <span>
                  When a new customer is provisioned, the backend attempts the tenant admin welcome email using
                  this platform SMTP configuration.
                </span>
              </div>
            </div>

            {isLoadingSmtp ? (
              <div className="platform-inline-alert is-neutral">
                Loading platform SMTP configuration...
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>

      <div className="platform-three-column">
        {settingsCards.map(({ title, description, action, icon: Icon }) => (
          <SectionCard
            key={title}
            title={title}
            action={
              <div className="platform-inline-link">
                {action}
                <ChevronRight size={14} />
              </div>
            }
          >
            <div className="platform-settings-card-body">
              <div className="platform-settings-icon">
                <Icon size={18} />
              </div>
              <p className="platform-page-description">{description}</p>
            </div>
          </SectionCard>
        ))}
      </div>

      <div className="platform-two-column">
        <SectionCard title="Control defaults">
          <div className="platform-stack">
            <div className="platform-setting-row">
              <div>
                <strong>Tenant activation requires review</strong>
                <p>Keep new tenants in draft until platform ops approves the setup.</p>
              </div>
              <span className="platform-toggle is-on" />
            </div>
            <div className="platform-setting-row">
              <div>
                <strong>Demo quota warning threshold</strong>
                <p>Notify the platform team when demo usage crosses the review threshold.</p>
              </div>
              <span className="platform-chip-row">
                <span className="platform-filter-chip is-active">70%</span>
              </span>
            </div>
            <div className="platform-setting-row">
              <div>
                <strong>Platform digest cadence</strong>
                <p>Review summary notifications for customer, quota, and health changes.</p>
              </div>
              <span className="platform-chip-row">
                <span className="platform-filter-chip is-active">Daily</span>
              </span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Reserved platform modules">
          <div className="platform-stack">
            {[
              { icon: Shield, title: "Security center", detail: "Platform IAM, audit export, and policy review." },
              { icon: Globe2, title: "Tenant network controls", detail: "Environment-level routing and future region controls." },
              { icon: Layers3, title: "Workspace templates", detail: "Default provisioning templates for new customers." },
              { icon: CalendarRange, title: "Release calendar", detail: "Operational planning and environment maintenance windows." },
            ].map(({ icon: Icon, title, detail }) => (
              <div className="platform-module-row" key={title}>
                <div className="platform-module-icon">
                  <Icon size={16} />
                </div>
                <div>
                  <strong>{title}</strong>
                  <p>{detail}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
